import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { GetSettings, UploadAsset } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Modal de Personalización Visual y Subida de Temas con SkiaSharp para "Arma la Playlist" (Tile ID 46).
 * Permite subir y previsualizar con Drag & Drop:
 *  1. Fondo Overlay 1536x180 px (overlay-1536)
 *  2. Fondo Overlay 1920x180 px (overlay-1920)
 *  3. Fondo Pantalla Gigante 1920x1080 px (screen-bg)
 *  4. Logotipo Central para Código QR 512x512 px (qr-logo)
 * 
 * @component PlaylistThemeModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está abierto.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al subir exitosamente un asset.
 */
export const PlaylistThemeModal = ({ isOpen, onClose, onSuccess }) => {
    const { showMessage } = useFloatMessage();

    // URLs actuales de la configuración
    const [themeAssets, setThemeAssets] = useState({
        overlay1536: '',
        overlay1920: '',
        screenBg: '',
        qrLogo: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [uploadingType, setUploadingType] = useState(null); // 'overlay-1536' | 'overlay-1920' | 'screen-bg' | 'qr-logo'
    const [dragOverCard, setDragOverCard] = useState(null);

    // Refs para inputs de archivo
    const fileInputRefs = {
        'overlay-1536': useRef(null),
        'overlay-1920': useRef(null),
        'screen-bg': useRef(null),
        'qr-logo': useRef(null)
    };

    // Cargar configuración existente al abrir
    const fetchCurrentTheme = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetSettings();
            if (res && res.success && res.data) {
                setThemeAssets({
                    overlay1536: res.data.overlayBackground1536Url || '',
                    overlay1920: res.data.overlayBackground1920Url || '',
                    screenBg: res.data.screenBackgroundUrl || '',
                    qrLogo: res.data.qrCenterLogoUrl || ''
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al cargar las imágenes actuales del tema.',
                message: 'Error al cargar las imágenes actuales del tema.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        if (isOpen) {
            fetchCurrentTheme();
        }
    }, [isOpen, fetchCurrentTheme]);

    // Cerrar con Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isOpen && !uploadingType) {
            onClose();
        }
    }, [isOpen, uploadingType, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen) return null;

    // Procesar subida de archivo
    const handleFileUpload = async (assetType, file) => {
        if (!file) return;

        setUploadingType(assetType);
        try {
            const res = await UploadAsset(assetType, file, true);

            if (res && res.success && res.data) {
                const newUrl = res.data.url;
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `🎨 Imagen para "${getAssetTitle(assetType)}" procesada y optimizada con éxito.`,
                    message: `🎨 Imagen para "${getAssetTitle(assetType)}" procesada y optimizada con éxito.`
                });

                setThemeAssets(prev => {
                    const next = { ...prev };
                    if (assetType === 'overlay-1536') next.overlay1536 = newUrl;
                    if (assetType === 'overlay-1920') next.overlay1920 = newUrl;
                    if (assetType === 'screen-bg') next.screenBg = newUrl;
                    if (assetType === 'qr-logo') next.qrLogo = newUrl;
                    return next;
                });

                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(res.data);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al procesar la imagen.',
                    message: (res && res.message) || 'Error al procesar la imagen.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al subir la imagen.',
                message: 'Error de conexión al subir la imagen.'
            });
        } finally {
            setUploadingType(null);
            setDragOverCard(null);
        }
    };

    // Drag & Drop handlers
    const handleDragOver = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCard(type);
    };

    const handleDragLeave = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragOverCard === type) {
            setDragOverCard(null);
        }
    };

    const handleDrop = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCard(null);

        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            handleFileUpload(type, file);
        }
    };

    const getAssetTitle = (type) => {
        switch (type) {
            case 'overlay-1536':
                return 'Fondo Overlay 1536x180';
            case 'overlay-1920':
                return 'Fondo Overlay 1920x180';
            case 'screen-bg':
                return 'Fondo Pantalla Gigante';
            case 'qr-logo':
                return 'Logotipo Central Código QR';
            default:
                return 'Asset Multimedia';
        }
    };

    // Tarjetas de configuración de los 4 assets requeridos
    const assetCards = [
        {
            type: 'overlay-1536',
            title: 'Fondo Overlay 1536x180 px',
            aspectRatio: '1536 × 180 px',
            formatInfo: 'WebP / PNG optimizado (Live Overlay Medio)',
            currentUrl: themeAssets.overlay1536,
            icon: 'bi-aspect-ratio',
            previewAspectClass: 'PlaylistThemeModal__Preview--banner'
        },
        {
            type: 'overlay-1920',
            title: 'Fondo Overlay 1920x180 px',
            aspectRatio: '1920 × 180 px',
            formatInfo: 'WebP / PNG optimizado (Live Overlay Full HD)',
            currentUrl: themeAssets.overlay1920,
            icon: 'bi-aspect-ratio-fill',
            previewAspectClass: 'PlaylistThemeModal__Preview--banner'
        },
        {
            type: 'screen-bg',
            title: 'Fondo Pantalla Gigante 1920x1080 px',
            aspectRatio: '1920 × 1080 px (16:9)',
            formatInfo: 'WebP calidad 90 (Pantalla Completa de Estadio)',
            currentUrl: themeAssets.screenBg,
            icon: 'bi-display',
            previewAspectClass: 'PlaylistThemeModal__Preview--screen'
        },
        {
            type: 'qr-logo',
            title: 'Logotipo Central Código QR 512x512 px',
            aspectRatio: '512 × 512 px (1:1)',
            formatInfo: 'PNG con canal alfa RGBA8888 (Escudo / Marca)',
            currentUrl: themeAssets.qrLogo,
            icon: 'bi-qr-code-scan',
            previewAspectClass: 'PlaylistThemeModal__Preview--logo'
        }
    ];

    return (
        <div className="PlaylistModal__Overlay" role="dialog" aria-modal="true">
            <div className="PlaylistModal__Backdrop" onClick={() => !uploadingType && onClose()} />
            <div className="PlaylistModal__Dialog PlaylistModal__Dialog--large">
                <div className="PlaylistModal__Content">
                    {/* Header */}
                    <div className="PlaylistModal__Header">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistModal__HeaderIcon PlaylistModal__HeaderIcon--theme">
                                <i className="bi bi-palette-fill text-danger"></i>
                            </span>
                            <div>
                                <h4 className="PlaylistModal__Title mb-0">Personalización Visual del Tema</h4>
                                <p className="PlaylistModal__Subtitle mb-0">
                                    Sube y optimiza fondos de pantalla, overlays de TV y logo central QR con SkiaSharp
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="PlaylistModal__CloseButton"
                            onClick={onClose}
                            disabled={!!uploadingType}
                            aria-label="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="PlaylistModal__Body PlaylistModal__Body--scrollable">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger" role="status"></div>
                                <p className="text-white-50 mt-2 small">Consultando recursos visuales...</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {assetCards.map((card) => {
                                    const isCurrentUploading = uploadingType === card.type;
                                    const isCardDragOver = dragOverCard === card.type;

                                    return (
                                        <div className="col-12 col-md-6" key={card.type}>
                                            <div
                                                className={`PlaylistThemeModal__Card ${
                                                    isCardDragOver ? 'PlaylistThemeModal__Card--dragOver' : ''
                                                } ${isCurrentUploading ? 'PlaylistThemeModal__Card--uploading' : ''}`}
                                                onDragOver={(e) => handleDragOver(e, card.type)}
                                                onDragLeave={(e) => handleDragLeave(e, card.type)}
                                                onDrop={(e) => handleDrop(e, card.type)}
                                            >
                                                {/* Header Card */}
                                                <div className="PlaylistThemeModal__CardHeader">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className={`bi ${card.icon} text-danger fs-5`}></i>
                                                        <div>
                                                            <h5 className="PlaylistThemeModal__CardTitle mb-0">
                                                                {card.title}
                                                            </h5>
                                                            <span className="PlaylistThemeModal__CardBadge">
                                                                {card.aspectRatio}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Card Description */}
                                                <p className="PlaylistThemeModal__CardDesc mb-2">
                                                    {card.formatInfo}
                                                </p>

                                                {/* Preview Thumbnail Box */}
                                                <div className={`PlaylistThemeModal__PreviewBox ${card.previewAspectClass}`}>
                                                    {card.currentUrl ? (
                                                        <img
                                                            src={card.currentUrl}
                                                            alt={card.title}
                                                            className="PlaylistThemeModal__PreviewImg"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="PlaylistThemeModal__NoImage">
                                                            <i className="bi bi-image text-white-50 fs-2"></i>
                                                            <span className="text-white-50 small mt-1">Sin imagen cargada</span>
                                                        </div>
                                                    )}

                                                    {isCurrentUploading && (
                                                        <div className="PlaylistThemeModal__UploadOverlay">
                                                            <div className="spinner-border text-light mb-2" role="status"></div>
                                                            <span className="small text-white fw-bold">Optimizando con SkiaSharp...</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Card Action / Dropzone Footer */}
                                                <div className="PlaylistThemeModal__CardFooter mt-2">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs[card.type]}
                                                        style={{ display: 'none' }}
                                                        accept="image/jpeg,image/png,image/webp"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                handleFileUpload(card.type, e.target.files[0]);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />

                                                    <button
                                                        type="button"
                                                        className="PlaylistThemeModal__UploadBtn"
                                                        onClick={() => fileInputRefs[card.type].current?.click()}
                                                        disabled={!!uploadingType}
                                                    >
                                                        <i className="bi bi-cloud-arrow-up-fill me-1"></i>
                                                        {card.currentUrl ? 'Reemplazar Imagen' : 'Subir Imagen'}
                                                    </button>

                                                    <span className="PlaylistThemeModal__DropHint">
                                                        o arrastra el archivo aquí
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="PlaylistModal__Footer">
                        <button
                            type="button"
                            className="PlaylistModal__Btn PlaylistModal__Btn--primary"
                            onClick={onClose}
                            disabled={!!uploadingType}
                        >
                            Listo / Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlaylistThemeModal;
